import type { Product, Seller, PaginatedResponse, Offer, OfferStatus, Bid, SystemStats, ChatMessage, ChatSession } from '../types';
import { dbService } from './db';
import { storageService } from './storage';
import { calculateDistance } from './geo';
import { monitoring } from './monitoring';

async function request<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method, credentials: 'same-origin', cache: 'no-store',
    headers: { 'X-Agro-Request': '1', ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(response.status === 401 && path !== '/auth/login' ? 'Unauthorized' : data.message || 'Request failed');
  return data;
}
const online = () => {
  if (!navigator.onLine) throw new Error('Connect to the internet to save changes. Nothing has been submitted.');
};
const callbacks = new Set<(data: any) => void>();
let events: EventSource | null = null;
function subscribe(callback: (data: any) => void) {
  callbacks.add(callback);
  if (!events && typeof EventSource !== 'undefined') {
    events = new EventSource('/api/events');
    events.onmessage = () => callbacks.forEach(fn => fn({ type: 'REFRESH' }));
  }
  return () => { callbacks.delete(callback); if (!callbacks.size) { events?.close(); events = null; } };
}

export const api = {
  base: '/api',
  auth: {
    register: (name: string, contact: string, password: string): Promise<Seller> => request('/auth/register', 'POST', { name, contact, password }),
    login: (contact: string, password: string): Promise<Seller> => request('/auth/login', 'POST', { contact, password }),
    refresh: (): Promise<Seller> => request('/auth/refresh', 'POST', {}),
    logout: async () => { await request('/auth/logout', 'POST', {}); },
    getSession: (): Promise<Seller | null> => navigator.onLine ? request('/auth/session') : Promise.resolve(null),
  },
  getProduct: async (id: string): Promise<Product> => {
    if (navigator.onLine) return request(`/products/${encodeURIComponent(id)}`);
    const found = (await dbService.getProducts()).find(p => p.id === id);
    if (!found) throw new Error('This product is not available offline.');
    return { ...found, isCached: true };
  },
  getProducts: async (page = 1, limit = 8, userLoc?: { lat: number; lng: number }): Promise<PaginatedResponse<Product>> => {
    let products: Product[];
    if (navigator.onLine) {
      products = await request<Product[]>('/products');
      await dbService.clearProducts();
      await dbService.saveProducts(products);
    } else products = (await dbService.getProducts()).map(p => ({ ...p, isCached: true }));
    if (userLoc) {
      products = products.map(p => p.latitude !== undefined && p.longitude !== undefined ? {
        ...p, distanceKm: calculateDistance(userLoc.lat, userLoc.lng, p.latitude, p.longitude),
      } : p).sort((a, b) => (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity));
    } else products.sort((a, b) => b.updatedAt - a.updatedAt);
    const start = (page - 1) * limit;
    return { data: products.slice(start, start + limit), page, totalPages: Math.ceil(products.length / limit), totalItems: products.length, hasMore: start + limit < products.length };
  },
  createProduct: async (data: Omit<Product, 'id' | 'seller' | 'isActive' | 'updatedAt'>, image?: Blob): Promise<Product> => {
    online();
    const imageUrl = image ? await storageService.upload(image) : data.imageUrl;
    return request('/products', 'POST', { ...data, imageUrl });
  },
  updateProduct: async (id: string, data: Partial<Omit<Product, 'id' | 'seller'>>, image?: Blob): Promise<Product> => {
    online();
    return request(`/products/${id}`, 'PUT', { ...data, ...(image ? { imageUrl: await storageService.upload(image) } : {}) });
  },
  toggleProductStatus: (id: string): Promise<Product> => { online(); return request(`/products/${id}/status`, 'PATCH', {}); },
  deleteProduct: async (id: string) => { online(); await request(`/products/${id}`, 'DELETE'); },
  getOffers: (id: string): Promise<Offer[]> => navigator.onLine ? request(`/products/${id}/offers`) : Promise.resolve([]),
  createOffer: (id: string, _name: string, _contact: string, offerPrice: number, message?: string): Promise<Offer> => {
    online(); return request(`/products/${id}/offers`, 'POST', { offerPrice, message });
  },
  updateOfferStatus: async (id: string, status: OfferStatus) => { online(); await request(`/offers/${id}`, 'PATCH', { status }); },
  getBids: (id: string): Promise<Bid[]> => navigator.onLine ? request(`/products/${id}/bids`) : Promise.resolve([]),
  placeBid: (id: string, _name: string, _contact: string, amount: number): Promise<Bid> => { online(); return request(`/products/${id}/bids`, 'POST', { amount }); },
  getChatSessions: (): Promise<ChatSession[]> => navigator.onLine ? request('/chats') : Promise.resolve([]),
  getMessages: (id: string): Promise<ChatMessage[]> => navigator.onLine ? request(`/chats/${encodeURIComponent(id)}/messages`) : Promise.resolve([]),
  sendMessage: (id: string, _recipient: string, text: string, productId: string, _productName: string): Promise<ChatMessage> => {
    online(); return request(`/chats/${encodeURIComponent(id)}/messages`, 'POST', { text, productId });
  },
  markChatAsRead: async (id: string) => { if (navigator.onLine) await request(`/chats/${encodeURIComponent(id)}/read`, 'POST', {}); },
  getSystemStats: async (): Promise<SystemStats> => {
    const start = performance.now();
    await request('/health');
    const stats = monitoring.getPerformanceStats();
    return { dbStatus: 'CONNECTED', dbType: 'PostgreSQL', cacheHitRate: 0, syncQueueSize: 0, latencyMs: Math.round(performance.now() - start), uptimeSeconds: stats.uptimeSeconds, requestsPerMinute: stats.requestsPerMinute, errorRate: stats.errorRate };
  },
  // Offline browsing is retained. Writes require the server; never pretend that a browser-only write was shared.
  syncOfflineActions: async () => {},
  onAuctionUpdate: subscribe,
  onChatUpdate: subscribe,
};
